import { Grid, Typography, Box, Avatar, Link } from "@mui/material";
import {
  ChangeEvent,
  FC,
  Fragment,
  useEffect,
  useMemo,
  useState,
  MouseEvent,
  useCallback,
} from "react";
import { useNavigate, useParams } from "react-router";
import {
  Instagram,
  LinkedIn,
  Twitter,
  Reddit,
  Sync,
} from "@mui/icons-material";
import AppCard from "../../components/cards/AppCard";
import { DataTable } from "../../components/tables/DataTable";
import {
  IUserDetailsColumns,
  IJoinedClubsDetails,
  IUserDetailsData,
  IUser,
} from "../../common/interfaces/user.interface";
import useFuse from "../../hooks/useFuse";
import {
  changeWhitelistingRequestStatus,
  getAllUserClubs,
  getUser,
  refreshDiscordUsername,
} from "../../api/users/user.api";
import {
  MonkeAdminPermissions,
  MonkeAdminStatus,
  WhitelistingAction,
  WhiteListingLastAction,
  WhitelistingStatus,
} from "../../common/enums/whitelisting.enum";
import {
  createNotification,
  getClubType,
  getTrimmedPublicKey,
  signTransactionMessage,
} from "../../utils/helpers";
import { encode } from "@project-serum/anchor/dist/cjs/utils/bytes/bs58";
import { ClubType } from "../../common/interfaces/club.interface";
import { MemberRole } from "../../common/enums/common.enums";
import copy from "copy-to-clipboard";
import clubAvatar from "../../assets/clubAvatar.png";
import KeyboardBackspaceTwoToneIcon from "@mui/icons-material/KeyboardBackspaceTwoTone";
import { USERS } from "../../common/constants/routes.constants";
import {
  GREEN_TEXT_COLOR,
  MESSAGE_TYPE,
  RED_TEXT_COLOR,
} from "../../utils/constants";
import {
  changeRequestStatus,
  getMonkeAdmins,
  getWhitelistings,
} from "../../solana/methods/whitelisting";
import { useAnchorWallet, useWallet } from "@solana/wallet-adapter-react";
import WhitelistingStatusChip from "../../components/WhitelistingStatus/WhitelistingStatus";
import { EMPTY_STRING } from "../../common/constants/common.constants";
import discordIcon from "../../assets/discord.svg";

const UserDetails: FC = () => {
  const params = useParams();
  const [loading, toggleLoading] = useState(false);
  const [page, setPage] = useState<number>(0);
  const [items, setItems] = useState<IUserDetailsData>();
  const [user, setUser] = useState<IUser>();
  const [search, setSearch] = useState<string>("");
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const navigate = useNavigate();
  const [hasPermission, toggleHasPermission] = useState(false);
  const wallet = useAnchorWallet();

  useEffect(() => {
    void getUsersTableInfo();
    void getUserInfo();
    if (wallet) {
      void checkIfHasPermission();
    }
  }, [wallet]);

  const { signMessage } = useWallet();

  const getUsersTableInfo = async () => {
    try {
      const { userAddress } = params;
      if (!userAddress) {
        throw new Error("Wrong club address");
      }
      toggleLoading(true);
      const response = await getAllUserClubs(userAddress);
      setItems(response);
      toggleLoading(false);
    } catch (error) {}
  };

  const checkIfHasPermission = async () => {
    try {
      const admins = await getMonkeAdmins(wallet!);
      console.log(admins);
      const hasPerm = !!admins.find(
        (config) =>
          config.admin.toString() === wallet!.publicKey.toString() &&
          !!config.permissions.find(
            (perm) =>
              Object.keys(perm)[0] === MonkeAdminPermissions.AddWhitelisting
          ) &&
          Object.keys(config.status)[0] === MonkeAdminStatus.Accepted
      );

      console.log(hasPerm);

      toggleHasPermission(hasPerm);
    } catch (error) {
      console.log(error);
    }
  };

  const handleDiscordRefresh = async (snowflake: string | undefined) => {
    const response = await refreshDiscordUsername(snowflake);
    if (user) {
      setUser({
        ...user,
        discordContact: response.username,
        updatedAt: new Date().toISOString(),
      });
    }
  };

  const getUserInfo = async () => {
    try {
      const { userAddress } = params;
      if (!userAddress) {
        throw new Error("Wrong club address");
      }
      toggleLoading(true);
      const response = await getUser(userAddress);
      const whitelistings = await getWhitelistings(wallet!);
      console.log(whitelistings);

      if (!!whitelistings.find((item) => item.toString() === userAddress)) {
        setUser({
          ...response,
          whitelisting: {
            ...response!.whitelisting,
            status: WhitelistingStatus.Approved,
          },
        });
      } else {
        setUser(response);
      }
      toggleLoading(false);
    } catch (error) {}
  };

  const handlePageChange = (
    event: MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const onRowsPerPageChange = (event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const goToClubDetailsInApp = (value) => {
    window.open(
      `https://app.unique.vc/club-details/${value.address}`,
      "_blank"
    );
  };

  const userTableColumns: IUserDetailsColumns[] = useMemo(() => {
    return [
      {
        id: "imageUrl",
        label: "Avatar",
        minWidth: 100,
        format: (value) => {
          return (
            <Avatar
              src={value === "" ? clubAvatar : value}
              alt="User avatar"
              style={{ backgroundColor: "transparent" }}
            />
          );
        },
      },
      {
        id: "name",
        label: "Club Name",
        minWidth: 170,
        format: (value) => {
          return value ? value : "N/A";
        },
      },
      {
        id: "role",
        label: "Role",
        minWidth: 150,
        format: (value) => {
          return getClubType(ClubType[value]);
        },
      },
      {
        id: "address",
        label: "Address",
        minWidth: 170,
        format: (value) => {
          return <Link>{getTrimmedPublicKey(value)}</Link>;
        },
      },
    ];
  }, []);

  const options = {
    includeScore: true,
    keys: userTableColumns.map((col) => col.id),
  };

  const filteredList: IJoinedClubsDetails[] = useFuse(
    items ? items.joinedClubs : [],
    search,
    options
  );

  const founder = useMemo(() => {
    return (
      items &&
      items.membershipPerRoles.find(
        (member) => member.role === MemberRole.FOUNDER
      )
    );
  }, [items]);

  const manager = useMemo(() => {
    return (
      items &&
      items.membershipPerRoles.find(
        (member) => member.role === MemberRole.MANAGER
      )
    );
  }, [items]);

  const member = useMemo(() => {
    return (
      items &&
      items.membershipPerRoles.find(
        (member) => member.role === MemberRole.MEMBER
      )
    );
  }, [items]);

  const approveOrDenyUser = useCallback(
    async (
      userPubkey: string,
      newStatus: WhitelistingStatus,
      whitelistingId: string
    ) => {
      try {
        let signedMessage: string | undefined;
        if (newStatus === WhitelistingStatus.Approved) {
          await changeRequestStatus(
            userPubkey,
            wallet!,
            WhitelistingAction.Add
          );
        } else {
          signedMessage = encode(
            await signTransactionMessage(
              "Change whitelisting status",
              signMessage
            )
          );
        }
        const updatedWhitelisting = await changeWhitelistingRequestStatus({
          newStatus: newStatus,
          pubkey: wallet?.publicKey.toString()!,
          whitelistingId,
          signedMessage,
        });

        setUser({
          ...updatedWhitelisting.user,
          whitelisting: updatedWhitelisting,
        });
        createNotification(MESSAGE_TYPE.SUCCESS, "Updated whitelisting!");
      } catch (error: any) {
        createNotification(
          MESSAGE_TYPE.ERROR,
          "Failed to change status",
          error.message
        );
        console.log(error);
      }
    },
    [wallet, items]
  );

  const removeUserFromWhitelisting = async () => {
    try {
      if (user && wallet && user.whitelisting.id) {
        await changeWhitelistingRequestStatus({
          newStatus: WhitelistingStatus.Denied,
          pubkey: user.pubKey,
          whitelistingId: user.whitelisting.id,
        });
      }
      if (user && wallet) {
        await changeRequestStatus(
          user?.pubKey,
          wallet,
          WhitelistingAction.Remove
        );
        setUser({
          ...user,
          whitelisting: {
            ...user.whitelisting,
            status: WhitelistingStatus.Denied,
          },
        });
      }
      createNotification(MESSAGE_TYPE.SUCCESS, "Updated whitelisting!");
    } catch (error) {
      console.log(error);
      createNotification(MESSAGE_TYPE.ERROR, "Failed to update whitelisting!");
    }
  };

  const handleSearch = (value: string) => {
    setSearch(value);
  };

  const formatDate = (dateString: string) => {
    const dateObj = new Date(dateString);
    const date = dateObj.toLocaleDateString(undefined, {
      month: "numeric",
      day: "numeric",
      year: "numeric",
    });
    return date;
  };

  const lastWhitelistingActionAndDate = () => {
    let action;
    if (
      user &&
      user.whitelisting &&
      user.whitelisting.discordSnowflake === ""
    ) {
      action = `${WhiteListingLastAction.Admin} ${formatDate(user?.updatedAt)}`;
    }
    if (user && user?.whitelisting?.status === WhitelistingStatus.Pending) {
      action = `${WhiteListingLastAction.Form} ${formatDate(user?.updatedAt)}`;
    }
    if (user && user?.whitelisting?.discordSnowflake !== "") {
      action = `${WhiteListingLastAction.Bot} ${formatDate(user?.updatedAt)}`;
    }
    return action;
  };

  return (
    <Fragment>
      {!loading && (
        <Box>
          <Grid
            container
            spacing={{ xs: 1, sm: 2, md: 4 }}
            columns={{ xs: 4, sm: 8, md: 12 }}
            justifyContent={"space-between"}
            alignItems={"center"}
            p={2}
          >
            <Grid item xs={12} sm={12} md={2}>
              <button
                style={{
                  display: "flex",
                  gap: "0.2em",
                  alignItems: "center",
                  fontSize: "0.75em",
                  transition: " opacity 0.3s ease-out",
                  width: "fit-content",
                  backgroundColor: "transparent",
                  border: "0px",
                  color: "white",
                  marginBottom: "0.7em",
                }}
                onClick={() => navigate(USERS)}
              >
                <KeyboardBackspaceTwoToneIcon fontSize={"small"} />
                <p>Back to users</p>
              </button>
              <img
                src={user?.imageUrl ? user?.imageUrl : clubAvatar}
                alt="user avatar"
                style={{
                  borderRadius: "20px",
                  width: "220px",
                  height: "220px",
                }}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={4}>
              <Grid
                container
                direction="column"
                gap={"8px"}
                justifyContent={"flex-start"}
                alignItems={"flex-start"}
                pl={2}
              >
                <Typography variant="h4" component="span" color={"primary"}>
                  {user && user.name}
                </Typography>
                <Typography
                  variant="body1"
                  component="span"
                  sx={{
                    "&:hover": {
                      cursor: "copy",
                    },
                  }}
                >
                  {user && (
                    <Link onClick={() => copy(user.pubKey)}>
                      {getTrimmedPublicKey(user.pubKey)}
                    </Link>
                  )}
                </Typography>
                <div>
                  {user && user.whitelisting?.discordSnowflake ? (
                    <Typography variant="body1" component="span">
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <img
                          src={discordIcon}
                          alt="discord"
                          style={{
                            filter: "invert(100%)",
                            marginTop: "-2px",
                          }}
                        />
                        {user.discordContact || "Not connected"}
                        <Sync
                          style={{
                            color: "white",
                            height: "20px",
                            cursor: "pointer",
                          }}
                          onClick={() =>
                            handleDiscordRefresh(
                              user.whitelisting?.discordSnowflake
                            )
                          }
                        />
                      </div>
                      {user.whitelisting.discordSnowflake}
                      <p style={{ fontSize: "0.7rem", opacity: 0.5 }}>
                        Last updated: {formatDate(user.updatedAt)}
                      </p>
                    </Typography>
                  ) : null}
                </div>
                <Typography variant="body1" component="span">
                  {user && user.email}
                </Typography>
                <Typography variant="body1" component="span">
                  {user && `Created: ${formatDate(user.createdAt)}`}
                </Typography>
                <Typography variant="body1" component="span">
                  {lastWhitelistingActionAndDate()}
                </Typography>
                <Typography
                  style={{ textTransform: "capitalize" }}
                  variant="body1"
                  component="span"
                >
                  {user && user.country}
                </Typography>
                <Grid item>
                  <Link color={"white"} target="_blank" href={user?.instagram}>
                    <Instagram sx={{ marginRight: "4px" }} />
                  </Link>
                  <Link color={"white"} target="_blank" href={user?.linkedin}>
                    <LinkedIn sx={{ marginRight: "4px" }} />
                  </Link>
                  <Link color={"white"} target="_blank" href={user?.twitter}>
                    <Twitter sx={{ marginRight: "4px" }} />
                  </Link>
                  <Link color={"white"} target="_blank" href={user?.reddit}>
                    <Reddit sx={{ marginRight: "4px" }} />
                  </Link>
                </Grid>
              </Grid>
            </Grid>
            {user && user.whitelisting && (
              <Grid item md={2}>
                {user?.whitelisting && (
                  <WhitelistingStatusChip
                    user={{ ...user, userName: user.name }}
                  />
                )}
                {user.whitelisting.status === WhitelistingStatus.Pending &&
                  hasPermission && (
                    <Box style={{ display: "flex", gap: "1em" }}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          approveOrDenyUser(
                            user.pubKey,
                            WhitelistingStatus.Approved,
                            user.whitelisting.id!
                          );
                        }}
                        style={{
                          background: "transparent",
                          color: GREEN_TEXT_COLOR,
                          border: `1px solid ${GREEN_TEXT_COLOR}`,
                          padding: "0.5em",
                          fontWeight: "600",
                          borderRadius: "8px",
                          marginTop: "1.5em",
                        }}
                      >
                        Approve
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          approveOrDenyUser(
                            user?.pubKey,
                            WhitelistingStatus.Denied,
                            user?.whitelisting.id!
                          );
                        }}
                        style={{
                          background: "transparent",
                          color: RED_TEXT_COLOR,
                          border: `1px solid ${RED_TEXT_COLOR}`,
                          padding: "0.5em",
                          fontWeight: "600",
                          borderRadius: "8px",
                          marginTop: "1.5em",
                        }}
                      >
                        Deny
                      </button>
                    </Box>
                  )}
                {user.whitelisting &&
                  user.whitelisting.status === WhitelistingStatus.Denied && (
                    <button
                      onClick={() =>
                        approveOrDenyUser(
                          user.pubKey,
                          WhitelistingStatus.Approved,
                          user.whitelisting.id!
                        )
                      }
                      style={{
                        background: "transparent",
                        color: GREEN_TEXT_COLOR,
                        border: `1px solid ${GREEN_TEXT_COLOR}`,
                        padding: "0.5em",
                        fontWeight: "600",
                        borderRadius: "8px",
                        width: "100%",
                        marginTop: "1.5em",
                      }}
                    >
                      Approve
                    </button>
                  )}
                {user.whitelisting &&
                  user.whitelisting.status === WhitelistingStatus.Approved &&
                  hasPermission && (
                    <button
                      onClick={removeUserFromWhitelisting}
                      style={{
                        background: "transparent",
                        color: RED_TEXT_COLOR,
                        border: `1px solid ${RED_TEXT_COLOR}`,
                        padding: "0.5em",
                        fontWeight: "600",
                        borderRadius: "8px",
                        width: "100%",
                        marginTop: "1.5em",
                      }}
                    >
                      Remove
                    </button>
                  )}
              </Grid>
            )}
            <Grid item xs={12} sm={12} md={2}>
              <AppCard
                label={"Number of joined clubs"}
                value={items?.numberOfJoinedClubs}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={2}>
              <AppCard
                label={"Total amount deposited"}
                value={items?.totalDepositedAmount}
              />
            </Grid>
          </Grid>
          {user &&
            user.whitelisting &&
            user.whitelisting.areasOfInterest &&
            user.whitelisting.domainOfExpertise && (
              <Grid
                container
                spacing={{ xs: 1, md: 4 }}
                columns={{ xs: 4, sm: 8, md: 12 }}
              >
                <Grid item xs={12} sm={12} md={6}>
                  <Box
                    style={{
                      padding: "5px 10px",
                    }}
                  >
                    <Typography>Areas of interest</Typography>
                    <Grid container columns={{ xs: 8 }}>
                      {user &&
                        user.whitelisting.areasOfInterest &&
                        user?.whitelisting.areasOfInterest.map((area) => {
                          return (
                            <Box
                              key={area}
                              style={{
                                border: "1px solid white",
                                borderRadius: "8px",
                                padding: "5px 10px",
                                marginLeft: "10px",
                              }}
                            >
                              <Typography>{area}</Typography>
                            </Box>
                          );
                        })}
                    </Grid>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={12} md={6}>
                  <Box>
                    <Typography>Domains of expertise</Typography>
                    <Grid container columns={{ xs: 8 }}>
                      {user?.whitelisting.domainOfExpertise &&
                        user?.whitelisting.domainOfExpertise.map((domain) => {
                          return (
                            <Box
                              key={domain}
                              style={{
                                border: "1px solid white",
                                borderRadius: "8px",
                                padding: "5px 10px",
                                marginLeft: "10px",
                              }}
                            >
                              <Typography>{domain}</Typography>
                            </Box>
                          );
                        })}
                    </Grid>
                  </Box>
                </Grid>
              </Grid>
            )}

          <Grid
            container
            spacing={{ xs: 1, md: 4 }}
            columns={{ xs: 4, sm: 8, md: 12 }}
            justifyContent={"space-evenly"}
            alignItems={"center"}
            p={2}
          >
            {user?.whitelisting &&
              user.whitelisting.interests &&
              user.whitelisting.personalBackground && (
                <>
                  <Grid item xs={12} sm={12} md={6}>
                    <AppCard
                      label={"MonkeVentures interests"}
                      value={
                        user && user?.whitelisting
                          ? user.whitelisting.interests
                          : EMPTY_STRING
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={6}>
                    <AppCard
                      label={"Personal background"}
                      value={
                        user && user?.whitelisting
                          ? user.whitelisting.personalBackground
                          : EMPTY_STRING
                      }
                    />
                  </Grid>
                </>
              )}
          </Grid>

          <Grid
            container
            spacing={{ xs: 1, md: 4 }}
            columns={{ xs: 4, sm: 8, md: 12 }}
            justifyContent={"space-evenly"}
            alignItems={"center"}
            p={2}
          >
            <Grid item xs={12} sm={12} md={4}>
              <AppCard label={"Founder"} value={founder ? founder.count : 0} />
            </Grid>
            <Grid item xs={12} sm={12} md={4}>
              <AppCard label={"Manager"} value={manager ? manager.count : 0} />
            </Grid>
            <Grid item xs={12} sm={12} md={4}>
              <AppCard label={"Member"} value={member ? member.count : 0} />
            </Grid>
          </Grid>
          <DataTable
            page={page}
            rowsPerPage={rowsPerPage}
            items={filteredList}
            columns={userTableColumns}
            itemsCount={items ? items?.joinedClubs.length : 0}
            onPageChange={handlePageChange}
            onSearch={handleSearch}
            onRowsPerPageChange={onRowsPerPageChange}
            onRowClick={goToClubDetailsInApp}
          />
        </Box>
      )}
    </Fragment>
  );
};

export default UserDetails;
